"use client";

import { ApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import posthog from "posthog-js";
import { TextInput } from "@/components/ui/TextInput";
import { ROLE_LONG_LABELS } from "@/lib/roles";
import type { Role } from "@/lib/types";
import {
  Building2,
  CheckCircle2,
  ClipboardCheck,
  Hash,
  Mail,
  MapPin,
  User,
  Users,
} from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { MAX_ADDRESS, MAX_EMAIL, MAX_FINESS, MAX_NAME } from "@/lib/field-limits";
import { CguConsent } from "@/components/legal/CguConsent";

// Inscription publique (fondateur d'officine) : volontairement limitée à ces
// trois rôles. Les autres rôles sont réservés à l'invitation d'un membre.
type RoleValue = Extract<Role, "titulaire" | "adjoint" | "preparateur">;

const ROLES: { value: RoleValue; label: string }[] = [
  { value: "titulaire", label: ROLE_LONG_LABELS.titulaire },
  { value: "adjoint", label: ROLE_LONG_LABELS.adjoint },
  { value: "preparateur", label: ROLE_LONG_LABELS.preparateur },
];

// Miroir du catalogue serveur (source de vérité : billing.constants.ts) —
// affiché à titre indicatif, le palier final est affecté par l'administrateur.
function suggestedPlan(teamSize: number): { label: string; members: string } {
  if (teamSize <= 3) return { label: "Petite officine", members: "≤ 3 personnes" };
  if (teamSize <= 8) return { label: "Officine moyenne", members: "4 à 8 personnes" };
  return { label: "Grande officine", members: "9 personnes et plus" };
}

const STEPS = ["Officine", "Vous", "Équipe"] as const;

export default function InscriptionPage() {
  const [step, setStep] = useState(0);
  const [officineName, setOfficineName] = useState("");
  const [address, setAddress] = useState("");
  const [finess, setFiness] = useState("");
  const [requesterName, setRequesterName] = useState("");
  const [requesterEmail, setRequesterEmail] = useState("");
  const [requesterRole, setRequesterRole] = useState<RoleValue | null>(null);
  const [teamSize, setTeamSize] = useState("");
  const [cguAccepted, setCguAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const finessOk = /^[0-9]{2}[0-9ABab][0-9]{6}$/.test(finess.trim());
  const teamSizeNum = Number(teamSize);
  const teamSizeOk = Number.isInteger(teamSizeNum) && teamSizeNum >= 1 && teamSizeNum <= 200;

  const stepOk = [
    officineName.trim().length > 0 && address.trim().length > 0 && finessOk,
    requesterName.trim().length > 0 &&
      /\S+@\S+\.\S+/.test(requesterEmail.trim()) &&
      requesterRole !== null,
    // Dernière étape : l'acceptation des CGU conditionne l'envoi au même titre
    // que l'effectif. Le serveur la réclame de toute façon (le DTO refuse tout
    // ce qui n'est pas `true`) — la bloquer ici évite un aller-retour dont le
    // message d'erreur serait moins clair que la case elle-même.
    teamSizeOk && cguAccepted,
  ][step];

  function next() {
    setError(null);
    if (step < STEPS.length - 1) setStep(step + 1);
    else void submit();
  }

  async function submit() {
    if (loading) return;
    setError(null);
    setLoading(true);
    try {
      await api.post(
        "/registration-requests",
        {
          officineName: officineName.trim(),
          address: address.trim(),
          finess: finess.trim().toUpperCase(),
          requesterName: requesterName.trim(),
          requesterEmail: requesterEmail.trim(),
          requesterRole,
          teamSize: teamSizeNum,
          // Un booléen, et rien d'autre : la version enregistrée est celle de
          // la constante serveur, jamais celle que le client annonce.
          cguAccepted,
        },
        { auth: false },
      );
      posthog.capture("registration_request_submitted", {
        requester_role: requesterRole,
        team_size: teamSizeNum,
      });
      setSent(true);
    } catch (e: unknown) {
      if (e instanceof ApiError && e.status === 400) {
        setError("Vérifiez les champs saisis (N° FINESS à 9 caractères, email valide).");
      } else if (e instanceof ApiError && e.status === 429) {
        // Le message de l'API porte le vrai délai : la limite par adresse
        // s'allonge à chaque essai, « une minute » serait souvent faux.
        setError(e.message || "Trop de tentatives — réessayez plus tard.");
      } else {
        posthog.captureException(e);
        setError("Envoi impossible pour le moment. Réessayez plus tard.");
      }
    } finally {
      setLoading(false);
    }
  }

  const plan = teamSizeOk ? suggestedPlan(teamSizeNum) : null;

  return (
    <div className="flex min-h-screen flex-1 items-center justify-center bg-bg px-6 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          {/* Logo détouré, posé sur le fond de page. Ici le titre ne nomme pas
              le produit : `alt` le porte donc, contrairement à l'écran de
              connexion. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/icons/logo-mark.png"
            alt="PharmacoWork"
            width={56}
            height={56}
            className="h-14 w-14"
          />
          <h1 className="text-[22px] font-bold text-text">Demande d&apos;inscription</h1>
          <p className="text-[14px] text-text-faint">
            Présentez votre officine : votre demande est examinée sous 48&nbsp;h, puis vous
            recevez un email d&apos;activation.
          </p>
        </div>

        <div className="rounded-card border border-border bg-surface p-5 shadow-card">
          {sent ? (
            <div className="flex flex-col items-center gap-2 py-2 text-center">
              <CheckCircle2 size={28} className="text-primary" />
              <p className="text-[15px] font-semibold text-text">Demande transmise</p>
              {/* Volontairement sans « nous examinons votre demande » : l'écran
                  ne sait pas si elle a été enregistrée. Une adresse déjà
                  rattachée à une officine est refusée, et l'API répond la même
                  chose dans tous les cas pour que ce formulaire public ne serve
                  pas à tester des adresses. C'est l'email qui dit la vraie
                  issue. */}
              <p className="text-[13px] text-text-faint">
                Un email vous a été envoyé à {requesterEmail.trim()}. Il confirme
                l&apos;enregistrement de votre demande pour «&nbsp;{officineName.trim()}&nbsp;»,
                ou vous explique pourquoi elle n&apos;a pas pu être enregistrée — par exemple
                si cette adresse est déjà associée à une officine.
              </p>
              <Link href="/connexion" className="mt-1 text-[13px] font-semibold text-primary">
                Retour à la connexion
              </Link>
            </div>
          ) : (
            <>
              {/* Stepper */}
              <div className="mb-5 flex items-center gap-2">
                {STEPS.map((label, i) => (
                  <div key={label} className="flex flex-1 flex-col items-center gap-1">
                    <div
                      className={`h-1.5 w-full rounded-full ${
                        i <= step ? "bg-primary" : "bg-surface-muted"
                      }`}
                    />
                    <span
                      className={`text-[11px] font-medium ${
                        i === step ? "text-primary" : "text-text-faint"
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                ))}
              </div>

              {step === 0 ? (
                <>
                  <label className="mb-1.5 block text-[13px] font-medium text-text-muted">
                    Nom de l&apos;officine
                  </label>
                  <TextInput
                    value={officineName}
                    onChange={(e) => setOfficineName(e.target.value)}
                    maxLength={MAX_NAME}
                    placeholder="Pharmacie du Centre"
                    icon={<Building2 size={18} strokeWidth={2} />}
                  />

                  <label className="mb-1.5 mt-4 block text-[13px] font-medium text-text-muted">
                    Adresse
                  </label>
                  <TextInput
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    maxLength={MAX_ADDRESS}
                    placeholder="12 rue de la République, 69001 Lyon"
                    icon={<MapPin size={18} strokeWidth={2} />}
                  />

                  <label className="mb-1.5 mt-4 block text-[13px] font-medium text-text-muted">
                    N° FINESS
                  </label>
                  <TextInput
                    value={finess}
                    onChange={(e) => setFiness(e.target.value)}
                    maxLength={MAX_FINESS}
                    placeholder="9 caractères — ex. 690012345"
                    icon={<Hash size={18} strokeWidth={2} />}
                  />
                  {finess.trim().length > 0 && !finessOk ? (
                    <p className="mt-1.5 text-[12px] text-text-faint">
                      Le N° FINESS comporte 9 caractères (celui de l&apos;établissement).
                    </p>
                  ) : null}
                </>
              ) : null}

              {step === 1 ? (
                <>
                  <label className="mb-1.5 block text-[13px] font-medium text-text-muted">
                    Votre nom complet
                  </label>
                  <TextInput
                    value={requesterName}
                    onChange={(e) => setRequesterName(e.target.value)}
                    maxLength={MAX_NAME}
                    placeholder="Prénom Nom"
                    icon={<User size={18} strokeWidth={2} />}
                  />

                  <label className="mb-1.5 mt-4 block text-[13px] font-medium text-text-muted">
                    Email professionnel
                  </label>
                  <TextInput
                    type="email"
                    value={requesterEmail}
                    onChange={(e) => setRequesterEmail(e.target.value)}
                    maxLength={MAX_EMAIL}
                    placeholder="vous@officine.fr"
                    icon={<Mail size={18} strokeWidth={2} />}
                  />

                  <label className="mb-1.5 mt-4 block text-[13px] font-medium text-text-muted">
                    Votre rôle dans l&apos;officine
                  </label>
                  <div className="flex flex-col gap-2">
                    {ROLES.map((r) => {
                      const active = requesterRole === r.value;
                      return (
                        <button
                          key={r.value}
                          type="button"
                          onClick={() => setRequesterRole(r.value)}
                          className={`flex items-center justify-between rounded-input border px-3.5 py-2.5 text-left text-[14px] transition-colors ${
                            active
                              ? "border-primary bg-primary-tint-2 font-semibold text-primary-strong"
                              : "border-border bg-surface font-medium text-text-muted"
                          }`}
                        >
                          {r.label}
                          {active ? <CheckCircle2 size={16} className="text-primary" /> : null}
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : null}

              {step === 2 ? (
                <>
                  <label className="mb-1.5 block text-[13px] font-medium text-text-muted">
                    Nombre de membres de l&apos;équipe (vous compris)
                  </label>
                  <TextInput
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={200}
                    value={teamSize}
                    onChange={(e) => setTeamSize(e.target.value)}
                    placeholder="Ex. 5"
                    icon={<Users size={18} strokeWidth={2} />}
                  />

                  {plan ? (
                    <div className="mt-4 flex items-center gap-2.5 rounded-input border border-primary-tint bg-primary-tint-2 px-3.5 py-3">
                      <ClipboardCheck
                        size={18}
                        strokeWidth={2}
                        className="shrink-0 text-primary-strong"
                      />
                      <div>
                        <p className="text-[13px] font-semibold text-primary-strong">
                          Palier indicatif : {plan.label}
                        </p>
                        <p className="text-[12px] text-primary-strong/80">
                          {plan.members} — le plan définitif est confirmé à la validation de
                          votre demande.
                        </p>
                      </div>
                    </div>
                  ) : null}

                  <CguConsent
                    className="mt-4"
                    checked={cguAccepted}
                    onChange={setCguAccepted}
                  />
                </>
              ) : null}

              {error ? <p className="mt-3 text-[12px] text-danger-deep">{error}</p> : null}

              <div className="mt-5 flex gap-2">
                {step > 0 ? (
                  <Button
                    variant="secondary"
                    className="flex-1"
                    disabled={loading}
                    onClick={() => {
                      setError(null);
                      setStep(step - 1);
                    }}
                  >
                    Retour
                  </Button>
                ) : null}
                <Button className="flex-1" disabled={loading || !stepOk} onClick={next}>
                  {step < STEPS.length - 1
                    ? "Continuer"
                    : loading
                      ? "Envoi…"
                      : "Envoyer ma demande"}
                </Button>
              </div>

              <p className="mt-4 text-center text-[13px] text-text-faint">
                Déjà un compte ?{" "}
                <Link href="/connexion" className="font-semibold text-primary">
                  Se connecter
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
